import React, { useState } from "react";
import { styled, SxProps, Box, Stack, Button, Link, Typography, Chip } from "@mui/material";

import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import AddIcon from "@mui/icons-material/Add";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import { ModalContext } from "../../../Modals";
import ModalAddAttachement, {
  ModalAddAttachementElementProps,
} from "../../../Modals/ModalAddAttachement/modal-add-attachment";
import { DbOrderBasketItemInfos } from "../../../../Core/Api/Types/db-order-basket-item-infos";
import OrderProductIcon from "../../../Misc/OrderProductIcon/order-product-icon";
import ModalCheckoutBillingOverride, {
  ModalCheckoutBillingOverrideProps,
} from "../../../Modals/ModalCheckoutBillingOverride/modal-checkout-billing-override";
import { DbCustomerInfos } from "../../../../Core/Api/Types/db-customer-infos";
import { GetOrderProductReadableType } from "../../../../Utils/helpers";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PeopleIcon from "@mui/icons-material/People";
import GavelIcon from "@mui/icons-material/Gavel";
import EditIcon from "@mui/icons-material/Edit";
import ApprovalIcon from "@mui/icons-material/Approval";
import { ItemField, RemoveItem, UpdateItemRootOnBasket } from "../../../../Redux/Reducers/redux-slice-basket";
import { fileService } from "../../../../Core/Api/services";
import { useAppDispatch } from "../../../../Redux/redux-store";
import ModalCheckoutContacts, {
  ModalCheckoutContactsProps,
} from "../../../Modals/ModalCheckoutContacts/modal-checkout-contacts";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { CurrencyTypes } from "../../../../Core/Api/Types/currency-types";
import { DropFileZoneContext } from "../../../Misc/DropFileZone/drop-file-zone";
import { useTranslation } from "react-i18next";

export type OrderRecapConfigProps = {
  className?: string;
  sx?: SxProps;
  item: DbOrderBasketItemInfos;
  customers: DbCustomerInfos[];
  actions?: { attachment?: boolean; overridePrice?: boolean; overrideCustomer?: boolean };
};

const OrderRecapConfig: React.FC<OrderRecapConfigProps> = (props) => {
  const { t } = useTranslation();
  const { item, customers, actions, ...otherProps } = props;

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [attachementFile, setAttachmentFile] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const handleAddAttachment = () => {
    if (!openModal) return;

    const onClose: ModalAddAttachementElementProps["onClose"] = (data) => {
      if (data.success) {
        setAttachmentFile(data.files[0].name);
        // Set transferId
        dispatch(
          UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.AttachmentTransfer, value: data.resourceId })
        );
      }
    };

    openModal<ModalAddAttachementElementProps>(ModalAddAttachement, {
      onClose,
      resourceId: item.id,
      context: DropFileZoneContext.OrderBasketCheckout,
    });
  };

  const handleRemoveAttachment = () => {
    if (!item.fileTransferId) return;

    fileService.DeleteFileFromItem(item.id).then((response) => {
      // Unset transferId
      dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: ItemField.AttachmentTransfer, value: null }));

      setAttachmentFile(null);
    });
  };

  const handleChangeBilling = () => {
    if (!openModal) return;

    const onClose: ModalCheckoutBillingOverrideProps["onClose"] = () => {};

    openModal<ModalCheckoutBillingOverrideProps>(ModalCheckoutBillingOverride, {
      onClose,
      closeModal: () => false,
      item: item,
      customers: customers,
      actions: actions,
    });
  };

  const handleChangeContacts = () => {
    if (!openModal) return;

    const onClose: ModalCheckoutContactsProps["onClose"] = () => {};

    openModal<ModalCheckoutContactsProps>(ModalCheckoutContacts, {
      onClose,
      closeModal: () => false,
      item: item,
    });
  };

  const handleRemoveItem = () => {
    dispatch(RemoveItem(item.id));
  };

  return (
    <Wrapper direction="row" alignItems="center" justifyContent="space-between" spacing={2} {...otherProps}>
      <Stack sx={{ flex: 0.9 }}>
        <Stack spacing={1} direction="row" alignItems="center">
          <OrderProductIcon type={item.selectedProduct?.type!} color="info" />
          <Typography variant="subtitle2" fontWeight={400}>
            {GetOrderProductReadableType(item.selectedProduct?.type!)}
          </Typography>
          {item.customerIdOverride && (
            <Chip
              icon={<PeopleIcon />}
              size="small"
              label={`${t("common:customer")} ${customers?.find((x) => x.id === item.customerIdOverride)?.name}`}
              sx={{ marginRight: 1 }}
            />
          )}
          {item.contractExternalDealId && (
            <Chip
              icon={<GavelIcon />}
              label={t("common:contractWithId", { id: item.contractExternalDealId })}
              sx={{ marginRight: 1 }}
            />
          )}
          {item.itemReferenceId && (
            <Chip
              icon={<EditIcon />}
              size="small"
              label={t("chip:label.underModificationWithId", { id: item.itemReferenceId })}
              sx={{ marginRight: 1 }}
            />
          )}
          {item.approvalRequired && (
            <Chip
              icon={<ApprovalIcon />}
              color="warning"
              size="small"
              label={t("chip:label.approvalRequiredWithMessage", { message: item.approvalRequiredMessage })}
              sx={{ marginRight: 1 }}
            />
          )}
        </Stack>
        <Box sx={{ color: "text.secondary" }} mt={1}>
          <Stack display="inline-flex" direction="row" spacing={0.5} mr={1.5}>
            <SettingsOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle2" color="text.secondary" fontSize="small">
              {item.objectLayer === ObjectLayer.Asset ? item.name + " - " : ""}
              {item.summarized}
              {item.episodeNb ? " for EP. " + item.episodeNb : ""}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={2} mt={1.5}>
          {attachementFile ? (
            <>
              <AttachFileOutlinedIcon fontSize="small" />
              <Typography color="text.primary" component="span" sx={{ pr: 0.5 }}>
                {attachementFile}
              </Typography>
              <Link component="button" underline="hover" color="error" onClick={handleRemoveAttachment}>
                {t("button:removeAttachment")}
              </Link>
            </>
          ) : (
            <>
              {actions && actions?.attachment && (
                <Link component="button" underline="hover" color="primary.text" onClick={handleAddAttachment}>
                  <AddIcon fontSize="small" />
                  {t("button:addAttachment")}
                </Link>
              )}
            </>
          )}
          {actions && (actions.overridePrice || actions.overrideCustomer) && (
            <Link component="button" underline="hover" color="primary.text" onClick={handleChangeBilling}>
              <MonetizationOnOutlinedIcon sx={{ mr: 0.5 }} />
              {t("common:billing")}
            </Link>
          )}
          <Link component="button" underline="hover" color="primary.text" onClick={handleChangeContacts}>
            <AlternateEmailIcon sx={{ mr: 0.5 }} />
            {item.billingEmails && item.billingEmails.length > 0 && (
              <>
                {item.billingEmails.length} email{item.billingEmails.length > 1 ? "s" : ""}
              </>
            )}
            {(!item.billingEmails || item.billingEmails.length === 0) && <>Add contact emails</>}
          </Link>
        </Stack>
      </Stack>
      <Stack justifyContent="space-between" spacing={2}>
        <Typography variant="subtitle2" fontWeight={400} color="text.secondary">
          {!item.overridenPrice ? item.totalPrice?.price : item.overridenPrice.price}
          {!item.overridenPrice
            ? item.totalPrice?.currency! === CurrencyTypes.EUR
              ? " €"
              : " $"
            : item.overridenPrice.currency! === CurrencyTypes.EUR
            ? " €"
            : " $"}
        </Typography>
        <Stack direction="row" spacing={1}>
          {/*<Button variant="icon" size="small">
            <NotificationsOffOutlinedIcon />
            </Button>*/}
          <Button variant="icon" size="small" onClick={handleRemoveItem}>
            <DeleteOutlineOutlinedIcon color="error" />
          </Button>
        </Stack>
      </Stack>
    </Wrapper>
  );
};

const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  background: theme.palette.backgroundAlpha.main,
}));

export default OrderRecapConfig;
